import { useState, useEffect, useCallback } from 'react';
import { loadGoogleMaps, onGoogleMapsReady, getGoogleMaps, isGoogleMapsLoading } from './GoogleMapsLoader.internal';

export type UseGoogleMapsOptions = Readonly<{
  /**
   * If true, loads Google Maps immediately on mount.
   * If false, returns a load() function to trigger loading manually.
   * @default false (lazy loading)
   */
  loadOnMount?: boolean;
}>;

export type UseGoogleMapsResult = Readonly<[typeof google.maps | null, { loading: boolean; load: () => void }]>;
/**
 * Hook for loading Google Maps API.
 *
 * @example
 * // Load immediately on mount
 * const [googleMaps, { loading }] = useGoogleMaps({ loadOnMount: true });
 *
 * @example
 * // Lazy load on user action
 * const [googleMaps, { loading, load }] = useGoogleMaps({ loadOnMount: false });
 * // Later: load();
 */
export const useGoogleMaps = (options: UseGoogleMapsOptions = {}): UseGoogleMapsResult => {
  const { loadOnMount = false } = options;

  const [googleMaps, setGoogleMaps] = useState<typeof google.maps | null>(() => getGoogleMaps());

  const [loading, setLoading] = useState(() => isGoogleMapsLoading());

  // Subscribe to loader state changes
  useEffect(() => {
    const unsubscribe = onGoogleMapsReady(maps => {
      setGoogleMaps(maps);
      setLoading(isGoogleMapsLoading());
    });

    return unsubscribe;
  }, []);

  // Load on mount if requested
  useEffect(() => {
    if (loadOnMount && !googleMaps) {
      loadGoogleMaps();
    }
  }, [loadOnMount, googleMaps]);

  // Expose load function
  const load = useCallback(() => {
    loadGoogleMaps();
  }, []);

  return [googleMaps, { loading, load }];
};
